import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/InvoicesService';
import { Invoice } from '../types';

export const useInvoice = (invoiceId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const invoiceQuery = useQuery({
        queryKey: ['invoice', invoiceId],
        queryFn: () => fetch(invoiceId!),
        enabled: !!invoiceId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: Invoice) => {
            const { createdByUsername, created, updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
        }
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: Invoice) => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            const { createdByUsername, created, updated, ...rest } = values;
            return update(invoiceId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return deleteRecord(invoiceId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
        }
    });

    return {
        record: invoiceQuery.data,
        loading: invoiceQuery.isLoading,
        fetchRecord: invoiceQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};